import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusText } from '~/eds/src/components/StatusText/StatusText';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';

import { OptimizeResult } from '../../../../../types/optimize';
import { mapTagToDescription } from '../../../utils';

interface Props {
  result: OptimizeResult;
}

export const BoostResultsOptionContent = ({ result }: Props) => {
  const intl = useIntl();
  const { config, noUserConfigChanges, stats, tag } = result;
  const { instructions } = config;
  const { correct, delta_correct, total } = stats;
  const description = mapTagToDescription(tag, intl);
  const correctDisplay =
    delta_correct < 0 ? `${delta_correct}` : `+${delta_correct}`;
  const correctStatus =
    delta_correct < 0 ? 'danger' : delta_correct > 0 ? 'success' : 'info';

  const initialCorrect = correct - delta_correct;
  const tooltip = intl.formatMessage(
    {
      id: 'BoostResultsOptionContent.accuracyTooltip',
      defaultMessage:
        'Boosted accuracy: {boostedAccuracy} out of {total} Previous accuracy: {previousAccuracy} out of {total}',
      description:
        'Tooltip showing accuracy comparison between boosted and previous model. Example: boostedAccuracy = "85", total = "100", previousAccuracy = "75"',
    },
    {
      boostedAccuracy: correct,
      total,
      previousAccuracy: initialCorrect,
    },
  );

  return (
    <Layout preset="subsections" pt={4}>
      <Text textAlign="left">{description}</Text>
      <Layout spacing={2} align="center">
        <StatusText
          status={correctStatus}
          text={correctDisplay}
          tooltip={tooltip}
          variant="banner"
        />
        <Box mb={-2}>
          <StatusText
            status="info"
            text={intl.formatMessage({
              id: 'BoostResultsOptionContent.correct',
              defaultMessage: 'Correct',
              description: 'Label for correct results status',
            })}
            tooltip={tooltip}
            variant="section"
          />
        </Box>
      </Layout>
      <FormField<string, false>
        readOnly
        input={TextArea}
        label={intl.formatMessage({
          id: 'BoostResultsOptionContent.instructions',
          defaultMessage: 'Instructions',
          description: 'Label for instructions field',
        })}
        name="boost-option-instructions"
        statusText={
          !noUserConfigChanges
            ? {
                status: 'ai' as const,
                text: intl.formatMessage({
                  id: 'BoostResultsOptionContent.aiSuggested',
                  defaultMessage: 'AI Suggested',
                  description: 'Status text indicating AI suggested content',
                }),
                icon: 'ai',
              }
            : undefined
        }
        value={instructions || ''}
        onChange={noop}
      />
    </Layout>
  );
};
